import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  WithFetch,
  FetchData,
  WithTableLayout,
  SORT_DIRECTION,
  TABLE_ACTION,
  TABLE_ACTION_STATE,
  SortModel
} from '@qrios/frontend-library';

import { Dialog } from '../../../../../common/dialog';
import endpoints from '../../../../../../api/endpoints';
import { PORTAL_PATH } from '../../../../../routes/paths';
import TableFooterInfo from '../../../../../common/table/footerInfo';
import { fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';

import locales from './locales';
import { columns } from './columns';
import { useEditApp } from './useEditApp';
import { useCreateApp } from './useCreateApp';
import EditUssdAppForm from './forms/editForm';
import CreateUssdAppForm from './forms/createForm';
import { getPermissionToCreateApps } from './helpers';
import { mapApiDataIntoTableFormat } from './dataTransformator';
import { UssdAppResponse, APP_CREATION_PERMISSION_STATUS } from './interfaces';
import Message from '../../../../../common/message';

const Apps = () => {
  const [sortModel, setSortModel] = useState<SortModel>([{ field: 'createDate', sort: SORT_DIRECTION.ASC }]);
  const [hasCreatePermission, setCreatePermission] = useState<boolean>(false);
  const history = useHistory();

  const {
    error: editError,
    initEditFormData,
    isEditAppLoading,
    isEditAppDialogOpen,
    handleOpenEditAppDialog,
    handleCloseEditAppDialog,
    handleSubmitEditAppDialog
  } = useEditApp();

  const {
    error: createError,
    isCreateAppLoading,
    isCreateAppDialogOpen,
    handleOpenCreateAppDialog,
    handleCloseCreateAppDialog,
    handleSubmitCreateAppDialog
  } = useCreateApp();

  const tableRowActions = {
    edit: handleOpenEditAppDialog,
    openInEditor: (appId: string) => {
      history.push({ pathname: PORTAL_PATH.USSD_EDITOR, search: `?appId=${appId}` });
    }
  };
  const handleCreateNewAppPermisions = (permissionStatus: APP_CREATION_PERMISSION_STATUS): void => {
    setCreatePermission(getPermissionToCreateApps(permissionStatus));
  };

  return (
    <>
      <WithFetch
        fetchProvider={fetchWithTokenRefresh}
        pathname={endpoints.ussdApps}
        withLoader={false}
        withError={false}
      >
        {({ data, refresh, loading, error, previousOptions }: FetchData<UssdAppResponse | undefined>) => {
          if (data) {
            handleCreateNewAppPermisions(data.limits.status);
          }

          if (loading === true) {
            setCreatePermission(false);
          }

          return (
            <WithTableLayout>
              <Table
                dataGridProps={{
                  rows: !data?.apps || loading ? [] : mapApiDataIntoTableFormat(data.apps, tableRowActions),
                  columns,
                  loading,
                  error: {
                    message: error,
                    refresh: () => refresh(previousOptions)
                  },
                  emptyMsg: locales.table.emptyMsg,
                  sortModel,
                  onSortModelChange: (model: SortModel) => setSortModel(model)
                }}
                headProps={{
                  warningMsg:
                    loading || !data || hasCreatePermission
                      ? undefined
                      : 'You reached the max number of owned USSD Apps.'
                }}
                footerProps={{
                  tableInfo: (
                    <TableFooterInfo
                      label={locales.footer.label}
                      tooltip={locales.footer.tooltip}
                      value={
                        !data || loading
                          ? '-'
                          : `${data.limits.createdLimit.itemsCount} of ${data.limits.createdLimit.limit}`
                      }
                    />
                  ),
                  createAppProps: {
                    disabled: loading,
                    name: TABLE_ACTION.CREATE,
                    handler: handleOpenCreateAppDialog,
                    state: hasCreatePermission ? TABLE_ACTION_STATE.ACTIVE : TABLE_ACTION_STATE.DISABLED,
                    warningMsg:
                      loading || !data || hasCreatePermission
                        ? undefined
                        : 'You reached the max number of owned USSD Apps.'
                  },
                  refreshProps: {
                    disabled: loading,
                    handler: () => {
                      refresh(previousOptions);
                    }
                  }
                }}
              />
              {initEditFormData && (
                <Dialog
                  handleClose={handleCloseEditAppDialog}
                  open={isEditAppDialogOpen}
                  title={locales.dialog.edit.title}
                >
                  <Message severity="success">
                    {({ setIsPopupOpen, setPopupMessage }) => (
                      <EditUssdAppForm
                        onClose={handleCloseEditAppDialog}
                        onSubmit={props => {
                          handleSubmitEditAppDialog(props, () => refresh(previousOptions));
                        }}
                        initialUssdAppFormData={initEditFormData}
                        loading={isEditAppLoading}
                        error={editError}
                        popup={{
                          setStateCallback: setIsPopupOpen,
                          setMsgCallback: setPopupMessage
                        }}
                      />
                    )}
                  </Message>
                </Dialog>
              )}
              <Dialog
                handleClose={handleCloseCreateAppDialog}
                open={isCreateAppDialogOpen}
                title={locales.dialog.create.title}
              >
                <Message severity="success">
                  {({ setIsPopupOpen, setPopupMessage }) => (
                    <CreateUssdAppForm
                      onClose={handleCloseCreateAppDialog}
                      onSubmit={props => {
                        handleSubmitCreateAppDialog(props, () => refresh(previousOptions));
                      }}
                      loading={isCreateAppLoading}
                      appIds={data ? data.apps.map(app => app.appId) : []}
                      error={createError}
                      popup={{
                        setStateCallback: setIsPopupOpen,
                        setMsgCallback: setPopupMessage
                      }}
                    />
                  )}
                </Message>
              </Dialog>
            </WithTableLayout>
          );
        }}
      </WithFetch>
    </>
  );
};

export default Apps;
